import { use, useEffect, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

type Props = {
  onSelectOrganisationUnit: (selectedNode: OrganisationTreeNode) => void;
  selectedOrganisationUnitIds: string[];
  initialVisibleOrganisationUnitId?: string;
};

type MultiSelectOrganizationTree = {
  isLoading: boolean;
  isSuccess: boolean;
  organisationUnitTree: OrganisationTreeNode[] | undefined;
  expandedNodes: string[];
  handleToggle: (event: React.SyntheticEvent | null, nodeIds: string[]) => void;
  handleSelect: (event: React.SyntheticEvent<Element, Event>, nodeId: string) => void;
  isIndeterminate: (node: OrganisationTreeNode) => boolean;
};

const useMultiSelectOrganizationTree = ({
  onSelectOrganisationUnit,
  selectedOrganisationUnitIds,
  initialVisibleOrganisationUnitId,
}: Props): MultiSelectOrganizationTree => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { organisationUnitTree, isLoading, isSuccess, error } = useOrganisationUnitTreeContext();

  const [nodesByNodeId, setNodesByNodeId] = useState(new Map<string, OrganisationTreeNode>());
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    const flattenOrganisationTree = (
      nodes: OrganisationTreeNode[] = [],
      map: Map<string, OrganisationTreeNode> = new Map(),
    ): Map<string, OrganisationTreeNode> => {
      for (const node of nodes) {
        map.set(node.id, node);
        if (node.childOrganisationUnits?.length) {
          flattenOrganisationTree(node.childOrganisationUnits, map);
        }
      }
      return map;
    };

    const flatNodeMap = flattenOrganisationTree(organisationUnitTree);
    setNodesByNodeId(flatNodeMap);

    initTree(organisationUnitTree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationUnitTree, initialVisibleOrganisationUnitId]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const initTree = (organisationUnits: OrganisationTreeNode[] | undefined): void => {
    if (!organisationUnits) {
      return;
    }

    const findParentOrganisationUnitIds = (
      nodes: OrganisationTreeNode[],
      organisationUnitId: string,
      parentOrganisationUnitIds: string[] = [],
    ): string[] | undefined => {
      for (const organisationUnit of nodes) {
        if (organisationUnit.id === organisationUnitId) {
          return parentOrganisationUnitIds;
        }

        const matchingParentOrganisationUnitIds = findParentOrganisationUnitIds(
          organisationUnit.childOrganisationUnits,
          organisationUnitId,
          [...parentOrganisationUnitIds, organisationUnit.id],
        );

        if (matchingParentOrganisationUnitIds) {
          return matchingParentOrganisationUnitIds;
        }
      }

      return undefined;
    };

    const parentOrganisationUnitIds = initialVisibleOrganisationUnitId
      ? findParentOrganisationUnitIds(organisationUnits, initialVisibleOrganisationUnitId)
      : undefined;

    setExpandedNodes(parentOrganisationUnitIds ?? organisationUnits.map((organisationUnit) => organisationUnit.id));
  };

  const handleToggle = (event: React.SyntheticEvent | null, nodeIds: string[]): void => {
    event?.persist?.();
    if (event?.target instanceof SVGElement || (event?.target as HTMLElement)?.className === 'MuiTreeItem-iconContainer') {
      setExpandedNodes(nodeIds);
    }
  };

  const handleSelect = (event: React.SyntheticEvent<Element, Event>, nodeId: string): void => {
    event.persist?.();
    if (event.target instanceof HTMLElement && event.target.className !== 'MuiTreeItem-iconContainer') {
      if (nodesByNodeId.has(nodeId)) {
        onSelect(nodeId);
      }
    }
  };

  const onSelect = (nodeId: string): void => {
    const selectedNode = nodesByNodeId.get(nodeId);
    if (selectedNode) onSelectOrganisationUnit(selectedNode);
  };

  const allChildrenAreSelected = (node: OrganisationTreeNode): boolean => {
    return node.childOrganisationUnits.every((child) => selectedOrganisationUnitIds.includes(child.id) && allChildrenAreSelected(child));
  };

  const isIndeterminate = (node: OrganisationTreeNode): boolean =>
    selectedOrganisationUnitIds.includes(node.id) && !allChildrenAreSelected(node);

  return {
    isLoading,
    isSuccess,
    organisationUnitTree,
    expandedNodes,
    handleToggle,
    handleSelect,
    isIndeterminate,
  };
};

export default useMultiSelectOrganizationTree;
